import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSuperadminAuth } from '../context/SuperadminAuthContext';
import ImageUploadPicker from '../components/shared/ImageUploadPicker';
import AnalyticsPanel from '../components/superadmin/AnalyticsPanel';

// ── tiny presentational helpers ─────────────────────────────────
const cardStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 14,
  padding: 20,
  color: '#E2E8F0',
};
const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.14)',
  background: 'rgba(15,23,42,0.6)', color: '#F1F5F9',
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4, letterSpacing: 0.3 };
const buttonPrimary = {
  padding: '9px 16px', borderRadius: 8, background: 'linear-gradient(135deg,#DC2626,#B91C1C)',
  color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const buttonGhost = {
  padding: '9px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.06)',
  color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.12)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

// ── Modal: Create Organisation ──────────────────────────────────
const PROTECTED_ORG_IDS = ['demo-contest-2026'];

// Same as the org app header: 32x32 circle, object-fit cover, white background.
const HeaderLogoPreview = ({ url }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
    <img data-testid="org-logo-header-preview" src={url || '/logo-symbol.png'} alt="Header preview"
      style={{ width: 32, height: 32, borderRadius: url ? '50%' : 0, objectFit: 'cover' }} />
    <span style={{ fontSize: 11, color: '#64748B' }}>How it looks in the app header</span>
  </div>
);

const CreateOrgModal = ({ onClose, onCreated, authedRequest, editOrg = null }) => {
  const isEdit = !!editOrg;
  const [form, setForm] = useState(isEdit
    ? { name: editOrg.name || '', domain: editOrg.domain || '', primary_color: editOrg.primary_color || '#4DB6AC', logo_url: editOrg.logo_url || '' }
    : { name: '', domain: '', primary_color: '#4DB6AC', logo_url: '' });
  // Uploaded logo lives in its own state. If both this and the manual
  // logo_url text field are filled, this one wins at submit — matches
  // the "uploaded image beats typed URL" precedence used elsewhere.
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Custom uploader so the picker's POST /api/upload call carries the
  // superadmin JWT instead of the (missing) org JWT that the default
  // axios interceptor would try to send.
  const uploader = useCallback(async (fd, { onUploadProgress }) => {
    const res = await authedRequest('post', '/upload', {
      data: fd,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return { url: res.data.url };
  }, [authedRequest]);

  const effectiveLogoUrl = uploadedLogoUrl || form.logo_url;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, primary_color: form.primary_color, logo_url: effectiveLogoUrl || null };
        const { data } = await authedRequest('put', `/superadmin/orgs/${editOrg.id}`, { data: payload });
        toast.success(`Organisation "${data.name}" updated`);
        onCreated(data);
        onClose();
        return;
      }
      const payload = { ...form, logo_url: effectiveLogoUrl };
      const { data } = await authedRequest('post', '/superadmin/orgs', { data: payload });
      toast.success(`Organisation "${data.name}" created`);
      onCreated(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? 'Failed to update organisation' : 'Failed to create organisation'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={isEdit ? 'Edit Organisation' : 'Create Organisation'} onClose={onClose} testid={isEdit ? 'edit-org-modal' : 'create-org-modal'}>
      <form onSubmit={submit}>
        {isEdit && (
          <div data-testid="org-id-readonly" style={{ fontSize: 11, color: '#64748B', marginBottom: 12 }}>
            ID: {editOrg.id} (can't be changed)
          </div>
        )}
        <label style={labelStyle}>Name</label>
        <input data-testid="org-name-input" required value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ ...inputStyle, marginBottom: 12 }} />
        <label style={labelStyle}>Domain (e.g. acme.com)</label>
        <input data-testid="org-domain-input" required value={form.domain} readOnly={isEdit}
          onChange={(e) => setForm({ ...form, domain: e.target.value })} style={{ ...inputStyle, marginBottom: isEdit ? 4 : 12, opacity: isEdit ? 0.6 : 1 }} />
        {isEdit && <div style={{ fontSize: 11, color: '#64748B', marginBottom: 12 }}>The domain is used for sign-in and can't be changed.</div>}
        <label style={labelStyle}>Primary colour</label>
        <input type="color" value={form.primary_color}
          onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
          style={{ ...inputStyle, padding: 4, height: 38, marginBottom: 12 }} />

        <label style={labelStyle}>Logo</label>
        {isEdit && <HeaderLogoPreview url={effectiveLogoUrl} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          {effectiveLogoUrl ? (
            <img
              data-testid="org-logo-preview"
              src={effectiveLogoUrl}
              alt="Logo preview"
              style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(15,23,42,0.6)' }}
            />
          ) : (
            <div style={{ width: 48, height: 48, borderRadius: 8, border: '1px dashed rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#64748B' }}>
              No logo
            </div>
          )}
          <ImageUploadPicker
            testId="org-logo-upload-btn"
            label={uploadingLogo ? 'Uploading…' : (uploadedLogoUrl ? 'Replace' : 'Upload logo')}
            disabled={uploadingLogo}
            uploader={uploader}
            onUploadStart={() => setUploadingLogo(true)}
            onUploadComplete={(url) => { setUploadedLogoUrl(url); setUploadingLogo(false); }}
            onUploadError={() => setUploadingLogo(false)}
          />
          {uploadedLogoUrl && (
            <button
              data-testid="org-logo-remove-btn"
              type="button"
              onClick={() => setUploadedLogoUrl('')}
              style={{ ...buttonGhost, padding: '8px 12px', fontSize: 12 }}
            >
              Remove upload
            </button>
          )}
          {isEdit && effectiveLogoUrl && (
            <button
              data-testid="org-logo-clear-btn"
              type="button"
              onClick={() => { setUploadedLogoUrl(''); setForm({ ...form, logo_url: '' }); }}
              style={{ ...buttonGhost, padding: '8px 12px', fontSize: 12 }}
            >
              Remove logo
            </button>
          )}
        </div>
        <label style={{ ...labelStyle, marginTop: 4 }}>…or paste a Logo URL (optional)</label>
        <input
          data-testid="org-logo-url-input"
          value={form.logo_url}
          onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
          placeholder="https://…"
          style={{ ...inputStyle, marginBottom: 4 }}
        />
        {uploadedLogoUrl && form.logo_url && (
          <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 16 }}>
            Uploaded image will be used — remove it to use the URL instead.
          </div>
        )}
        {!(uploadedLogoUrl && form.logo_url) && <div style={{ marginBottom: 16 }} />}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={buttonGhost}>Cancel</button>
          <button data-testid={isEdit ? 'org-edit-submit' : 'org-create-submit'} type="submit" disabled={submitting || uploadingLogo} style={buttonPrimary}>
            {isEdit ? (submitting ? 'Saving…' : 'Save changes') : (submitting ? 'Creating…' : 'Create')}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

// ── Modal: Deactivate / Delete organisation ─────────────────────
const LINKED_LABELS = { users: 'users', posts: 'posts', challenges: 'actions', impact_entries: 'impact entries', notifications: 'notifications', activity: 'activity records', audit_logs: 'audit entries' };

const OrgStatusModal = ({ org, onClose, onDone, authedRequest }) => {
  const [linked, setLinked] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    authedRequest('get', `/superadmin/orgs/${org.id}/linked-data`)
      .then(({ data }) => setLinked(data))
      .catch((err) => { toast.error(err.response?.data?.detail || 'Failed to load organisation data'); onClose(); });
  }, [org.id, authedRequest, onClose]);

  const hardDelete = linked && !linked.has_data;
  const nameOk = typed.trim() === (org.name || '').trim();
  const run = async () => {
    setBusy(true);
    try {
      if (hardDelete) {
        await authedRequest('delete', `/superadmin/orgs/${org.id}`, { data: { confirm_name: typed } });
        toast.success(`Deleted ${org.name}`);
      } else {
        await authedRequest('post', `/superadmin/orgs/${org.id}/deactivate`, { data: { confirm_name: typed } });
        toast.success(`${org.name} deactivated`);
      }
      onDone();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Action failed'));
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={hardDelete ? 'Delete organisation' : 'Deactivate organisation'} onClose={onClose} testid="org-status-modal">
      {!linked ? <div style={{ color: '#64748B', fontSize: 13 }}>Checking linked data…</div> : (
        <>
          {hardDelete ? (
            <p data-testid="org-status-explainer" style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.5 }}>
              <b>{org.name}</b> has no users or data. Deleting it removes the organisation permanently. This cannot be undone.
            </p>
          ) : (
            <div data-testid="org-status-explainer" style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.5 }}>
              <p style={{ marginTop: 0 }}><b>{org.name}</b> has data, so it can't be deleted. Deactivating it will:</p>
              <ul style={{ paddingLeft: 18, margin: '0 0 8px' }}>
                <li>stop all its users from logging in, and sign out anyone already logged in</li>
                <li>hide it from sign-up and the normal organisation list</li>
                <li>keep everything ({Object.entries(linked.counts).map(([k, n]) => `${n} ${LINKED_LABELS[k] || k}`).join(', ')}), so it can be reactivated later</li>
              </ul>
            </div>
          )}
          <label style={labelStyle}>Type the organisation name to confirm: {org.name}</label>
          <input data-testid="org-status-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)} style={{ ...inputStyle, marginBottom: 14 }} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={buttonGhost}>Cancel</button>
            <button data-testid="org-status-submit" type="button" disabled={!nameOk || busy} onClick={run}
              style={{ ...buttonPrimary, opacity: !nameOk || busy ? 0.5 : 1, cursor: !nameOk || busy ? 'not-allowed' : 'pointer' }}>
              {busy ? 'Working…' : hardDelete ? 'Delete permanently' : 'Deactivate'}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
};

// ── Modal: Create User ──────────────────────────────────────────
const CreateUserModal = ({ onClose, onCreated, authedRequest, orgs }) => {
  const [form, setForm] = useState({
    name: '', email: '', role: 'EMPLOYEE',
    org_id: orgs[0]?.id || '', password: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await authedRequest('post', '/superadmin/users', { data: form });
      toast.success(
        data.generated_password
          ? `User created. Temp password: ${data.generated_password}`
          : `User ${data.email} created`,
        { duration: 8000 }
      );
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Create User" onClose={onClose} testid="create-user-modal">
      <form onSubmit={submit}>
        <label style={labelStyle}>Name</label>
        <input data-testid="user-name-input" required value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ ...inputStyle, marginBottom: 12 }} />
        <label style={labelStyle}>Email</label>
        <input data-testid="user-email-input" required type="email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} style={{ ...inputStyle, marginBottom: 12 }} />
        <label style={labelStyle}>Organisation</label>
        <select data-testid="user-org-select" required value={form.org_id}
          onChange={(e) => setForm({ ...form, org_id: e.target.value })} style={{ ...inputStyle, marginBottom: 12 }}>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.domain})</option>)}
        </select>
        <label style={labelStyle}>Role</label>
        <select data-testid="user-role-select" value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })} style={{ ...inputStyle, marginBottom: 12 }}>
          <option value="EMPLOYEE">Employee</option>
          <option value="USER">User</option>
          <option value="HR_ADMIN">HR Admin</option>
        </select>
        <label style={labelStyle}>Password (blank = auto-generate &amp; email)</label>
        <input data-testid="user-password-input" type="text" value={form.password} placeholder="leave blank to auto-generate"
          onChange={(e) => setForm({ ...form, password: e.target.value })} style={{ ...inputStyle, marginBottom: 20 }} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={buttonGhost}>Cancel</button>
          <button data-testid="user-create-submit" type="submit" disabled={submitting} style={buttonPrimary}>
            {submitting ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

// ── Modal: Bulk CSV upload ─────────────────────────────────────
const BulkUploadModal = ({ onClose, onUploaded, authedRequest }) => {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await authedRequest('post', '/superadmin/users/bulk-upload', {
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      const bdayMsg = data.birthdays_updated
        ? ` · Birthdays: ${data.birthdays_updated} updated`
        : '';
      toast.success(`Created ${data.created} user(s); ${data.failed} failure(s).${bdayMsg}`);
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Bulk CSV upload" onClose={onClose} testid="bulk-upload-modal" width={560}>
      <p style={{ fontSize: 13, color: '#94A3B8', margin: '0 0 8px', lineHeight: 1.6 }}>
        Required columns: <code style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4 }}>name, email, role, org_id</code>.
        Each new user gets an auto-generated password emailed to them.
      </p>
      <p style={{ fontSize: 13, color: '#94A3B8', margin: '0 0 14px', lineHeight: 1.6 }}>
        Optional birthday columns: <code style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4 }}>birth_day</code> (1–31) and <code style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4 }}>birth_month</code> (1–12) — both must be provided together. A row with just <code style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4 }}>email + birth_day + birth_month</code> (other columns blank) updates the birthday of an existing user matched by email — it will not create a new user.
      </p>
      <form onSubmit={submit}>
        <input data-testid="bulk-csv-input" type="file" accept=".csv" required
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          style={{ ...inputStyle, padding: 8, marginBottom: 16 }} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: 16 }}>
          <button type="button" onClick={onClose} style={buttonGhost}>Close</button>
          <button data-testid="bulk-upload-submit" type="submit" disabled={!file || submitting} style={buttonPrimary}>
            {submitting ? 'Uploading…' : 'Upload CSV'}
          </button>
        </div>
      </form>

      {result && (
        <div data-testid="bulk-upload-result" style={{
          borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 14, fontSize: 13,
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 10 }}>
            <span style={{ color: '#86EFAC' }}>✓ Created: <strong>{result.created}</strong></span>
            <span style={{ color: '#FCA5A5' }}>✗ Failed: <strong>{result.failed}</strong></span>
            {(result.birthdays_updated ?? 0) > 0 && (
              <span style={{ color: '#93C5FD' }}>🎂 Birthdays: <strong>{result.birthdays_updated}</strong></span>
            )}
            {result.birthdays_unmatched?.length > 0 && (
              <span style={{ color: '#FCD34D' }}>? Unmatched: <strong>{result.birthdays_unmatched.length}</strong></span>
            )}
            {result.birthdays_invalid?.length > 0 && (
              <span style={{ color: '#FCA5A5' }}>! Invalid dates: <strong>{result.birthdays_invalid.length}</strong></span>
            )}
          </div>
          {result.failures?.length > 0 && (
            <div style={{ maxHeight: 200, overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
              {result.failures.map((f, i) => (
                <div key={i} style={{ fontSize: 12, color: '#FCA5A5', marginBottom: 4 }}>
                  Row {f.row} ({f.email || '—'}): {f.reason}
                </div>
              ))}
            </div>
          )}
          {result.birthdays_unmatched?.length > 0 && (
            <div style={{ maxHeight: 160, overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: '#FCD34D', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Birthday rows with no matching user</div>
              {result.birthdays_unmatched.map((f, i) => (
                <div key={i} style={{ fontSize: 12, color: '#FCD34D', marginBottom: 4 }}>
                  Row {f.row}: {f.email || '—'}
                </div>
              ))}
            </div>
          )}
          {result.birthdays_invalid?.length > 0 && (
            <div style={{ maxHeight: 160, overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 11, color: '#FCA5A5', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Invalid birthday values</div>
              {result.birthdays_invalid.map((f, i) => (
                <div key={i} style={{ fontSize: 12, color: '#FCA5A5', marginBottom: 4 }}>
                  Row {f.row} ({f.email || '—'}): {f.reason}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </ModalShell>
  );
};

// ── Generic modal shell ─────────────────────────────────────────
const ModalShell = ({ title, onClose, children, testid, width = 460 }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000, padding: 16,
    }}
  >
    <div
      data-testid={testid}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%', maxWidth: width,
        background: '#1E293B', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 14, padding: 28, color: '#F1F5F9',
        maxHeight: '90vh', overflowY: 'auto',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{title}</h2>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: '#94A3B8',
          fontSize: 22, cursor: 'pointer', padding: 0, lineHeight: 1,
        }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

// ── Main Dashboard ──────────────────────────────────────────────
const SuperadminDashboardPage = () => {
  const { user, logout, authedRequest } = useSuperadminAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [tab, setTab] = useState('orgs');
  const [editingOrg, setEditingOrg] = useState(null);
  const [statusOrg, setStatusOrg] = useState(null);

  const loadOrgs = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const { data } = await authedRequest('get', '/superadmin/orgs');
      setOrgs(data);
      setSelectedOrgId((prev) => prev || data[0]?.id || null);
    } catch (err) {
      toast.error('Failed to load organisations');
    } finally {
      setLoadingOrgs(false);
    }
  }, [authedRequest]);

  const loadUsers = useCallback(async (orgId) => {
    if (!orgId) return;
    setLoadingUsers(true);
    try {
      const { data } = await authedRequest('get', `/superadmin/orgs/${orgId}/users`);
      setUsers(data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, [authedRequest]);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);
  useEffect(() => { loadUsers(selectedOrgId); }, [selectedOrgId, loadUsers]);

  const handleReactivateOrg = async (org) => {
    if (!window.confirm(`Reactivate "${org.name}"? Its users will be able to log in again.`)) return;
    try {
      await authedRequest('post', `/superadmin/orgs/${org.id}/reactivate`);
      toast.success(`${org.name} reactivated`);
      await loadOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate organisation');
    }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"?`)) return;
    try {
      await authedRequest('delete', `/superadmin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      await loadUsers(selectedOrgId);
      await loadOrgs(); // refresh user_count
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/superadmin/login', { replace: true });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0F172A', color: '#E2E8F0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Top bar */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'rgba(15,23,42,0.85)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 11, color: '#FCA5A5', fontWeight: 700, letterSpacing: 1 }}>SUPERADMIN CONSOLE</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#F1F5F9' }}>Platform Management</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span data-testid="superadmin-user-email" style={{ fontSize: 13, color: '#94A3B8' }}>{user?.email}</span>
          <button data-testid="superadmin-logout-btn" onClick={handleLogout} style={buttonGhost}>Sign out</button>
        </div>
      </header>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '28px' }}>
        {/* KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
          <KPI label="Organisations" value={orgs.length} />
          <KPI label="Total users" value={orgs.reduce((s, o) => s + (o.user_count || 0), 0)} />
          <KPI label="Active orgs" value={orgs.filter((o) => o.is_active !== false).length} />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
          {[['orgs', 'Organisations'], ['analytics', 'Analytics']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`superadmin-tab-${k}`}
              style={{ ...buttonGhost, background: tab === k ? 'rgba(220, 38, 38, 0.18)' : 'transparent', borderColor: tab === k ? 'rgba(220, 38, 38, 0.4)' : undefined }}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'analytics' && <AnalyticsPanel orgs={orgs} authedRequest={authedRequest} />}

        {tab === 'orgs' && (<>
        {/* Action bar */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
          <button data-testid="open-create-org" onClick={() => setShowOrgModal(true)} style={buttonPrimary}>
            + New organisation
          </button>
          <button data-testid="open-create-user" onClick={() => setShowUserModal(true)} disabled={!orgs.length} style={buttonGhost}>
            + New user
          </button>
          <button data-testid="open-bulk-upload" onClick={() => setShowBulkModal(true)} disabled={!orgs.length} style={buttonGhost}>
            ↑ Bulk CSV upload
          </button>
        </div>

        {/* Two-column: Orgs list + Users table */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 360px) 1fr', gap: 20 }}>
          {/* Organisations */}
          <section style={cardStyle} data-testid="orgs-panel">
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', margin: '0 0 14px', letterSpacing: 0.4 }}>
              ORGANISATIONS
            </h3>
            {loadingOrgs ? (
              <div style={{ color: '#64748B', fontSize: 13 }}>Loading…</div>
            ) : orgs.length === 0 ? (
              <div style={{ color: '#64748B', fontSize: 13 }}>No organisations yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {orgs.map((o) => {
                  const active = o.id === selectedOrgId;
                  return (
                    <div key={o.id}
                      data-testid={`org-row-${o.id}`}
                      onClick={() => setSelectedOrgId(o.id)}
                      style={{
                        padding: '10px 12px', borderRadius: 9, cursor: 'pointer',
                        background: active ? 'rgba(220, 38, 38, 0.18)' : 'rgba(255,255,255,0.03)',
                        border: active ? '1px solid rgba(220, 38, 38, 0.4)' : '1px solid transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#F1F5F9' }}>
                          {o.name}
                          {o.is_active === false && <span data-testid={`org-inactive-${o.id}`} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#FCA5A5', background: 'rgba(220,38,38,0.18)', padding: '2px 6px', borderRadius: 6 }}>INACTIVE</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                          {o.domain} · {o.user_count} users
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <button
                          data-testid={`org-edit-${o.id}`}
                          onClick={(e) => { e.stopPropagation(); setEditingOrg(o); }}
                          style={{ ...buttonGhost, padding: '4px 8px', fontSize: 11 }}
                        >Edit</button>
                        {!PROTECTED_ORG_IDS.includes(o.id) && (o.is_active === false ? (
                          <button
                            data-testid={`org-reactivate-${o.id}`}
                            onClick={(e) => { e.stopPropagation(); handleReactivateOrg(o); }}
                            style={{ ...buttonGhost, padding: '4px 8px', fontSize: 11 }}
                          >Reactivate</button>
                        ) : (
                          <button
                            data-testid={`org-delete-${o.id}`}
                            onClick={(e) => { e.stopPropagation(); setStatusOrg(o); }}
                            style={{ ...buttonGhost, padding: '4px 8px', fontSize: 11, color: '#FCA5A5' }}
                            title="Deactivate or delete organisation"
                          >Remove</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Users */}
          <section style={cardStyle} data-testid="users-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', margin: 0, letterSpacing: 0.4 }}>
                USERS {selectedOrgId && `· ${orgs.find((o) => o.id === selectedOrgId)?.name || ''}`}
              </h3>
              <span style={{ fontSize: 12, color: '#64748B' }}>{users.length} total</span>
            </div>
            {!selectedOrgId ? (
              <div style={{ color: '#64748B', fontSize: 13 }}>Select an organisation to view its users.</div>
            ) : loadingUsers ? (
              <div style={{ color: '#64748B', fontSize: 13 }}>Loading…</div>
            ) : users.length === 0 ? (
              <div style={{ color: '#64748B', fontSize: 13 }}>No users in this organisation.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#64748B', fontSize: 11, letterSpacing: 0.4 }}>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>NAME</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>EMAIL</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>ROLE</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>STARS</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} data-testid={`user-row-${u.id}`}>
                        <td style={{ padding: '10px', color: '#F1F5F9' }}>{u.username || '—'}</td>
                        <td style={{ padding: '10px', color: '#CBD5E1' }}>{u.email}</td>
                        <td style={{ padding: '10px' }}>
                          <span style={{
                            fontSize: 11, padding: '3px 8px', borderRadius: 999,
                            background: u.role === 'HR_ADMIN' ? 'rgba(220, 38, 38, 0.18)' : 'rgba(77, 182, 172, 0.15)',
                            color: u.role === 'HR_ADMIN' ? '#FCA5A5' : '#5EEAD4',
                          }}>{u.role}</span>
                        </td>
                        <td style={{ padding: '10px', color: '#94A3B8' }}>{u.social_score ?? 0}</td>
                        <td style={{ padding: '10px', textAlign: 'right' }}>
                          <button data-testid={`user-delete-${u.id}`}
                            onClick={() => handleDeleteUser(u)}
                            style={{
                              background: 'none', border: 'none', color: '#64748B',
                              cursor: 'pointer', fontSize: 13, padding: '4px 8px',
                            }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
        </>)}
      </div>

      {showOrgModal && (
        <CreateOrgModal
          onClose={() => setShowOrgModal(false)}
          onCreated={(o) => { setOrgs((prev) => [o, ...prev]); setSelectedOrgId(o.id); }}
          authedRequest={authedRequest}
        />
      )}
      {editingOrg && (
        <CreateOrgModal
          editOrg={editingOrg}
          onClose={() => setEditingOrg(null)}
          onCreated={(o) => setOrgs((prev) => prev.map((x) => (x.id === o.id ? { ...x, ...o } : x)))}
          authedRequest={authedRequest}
        />
      )}
      {statusOrg && (
        <OrgStatusModal
          org={statusOrg}
          onClose={() => setStatusOrg(null)}
          onDone={async () => { if (selectedOrgId === statusOrg.id) { setSelectedOrgId(null); setUsers([]); } await loadOrgs(); }}
          authedRequest={authedRequest}
        />
      )}
      {showUserModal && (
        <CreateUserModal
          onClose={() => setShowUserModal(false)}
          onCreated={() => { loadOrgs(); loadUsers(selectedOrgId); }}
          authedRequest={authedRequest}
          orgs={orgs}
        />
      )}
      {showBulkModal && (
        <BulkUploadModal
          onClose={() => setShowBulkModal(false)}
          onUploaded={() => { loadOrgs(); loadUsers(selectedOrgId); }}
          authedRequest={authedRequest}
        />
      )}
    </div>
  );
};

const KPI = ({ label, value }) => (
  <div style={{ ...cardStyle, padding: 16 }}>
    <div style={{ fontSize: 11, color: '#64748B', fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>{label.toUpperCase()}</div>
    <div style={{ fontSize: 26, fontWeight: 700, color: '#F1F5F9' }}>{value}</div>
  </div>
);

export default SuperadminDashboardPage;
